import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useI18n } from '../content/i18n';
import './LanguageSecretSection.css';

gsap.registerPlugin(ScrollTrigger);

const LanguageSecretSection = () => {
  const { t, isKo } = useI18n();
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const videoRef = useRef(null);
  const videoElementRef = useRef(null);
  const subtitleRef = useRef(null);
  const imageSliderRef = useRef(null);
  const textSide2Ref = useRef(null);
  const [isMuted, setIsMuted] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const images = ['/resource/blind-1.png', '/resource/blind-2.png'];

  useEffect(() => {
    const ctx = gsap.context(() => {
      // 타이틀 애니메이션
      gsap.fromTo(
        titleRef.current,
        { 
          opacity: 0, 
          y: 80,
          scale: 0.9
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 1.2,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
            end: 'top 40%',
            scrub: 1,
          },
        }
      );

      // 비디오 애니메이션
      gsap.fromTo(
        videoRef.current,
        { 
          opacity: 0, 
          y: 60,
          scale: 0.95
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: videoRef.current,
            start: 'top 80%',
            end: 'top 50%',
            scrub: 1,
          },
        }
      );

      // 서브타이틀 애니메이션
      gsap.fromTo(
        subtitleRef.current,
        { 
          opacity: 0, 
          y: 30
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: subtitleRef.current,
            start: 'top 85%',
            end: 'top 70%',
            scrub: 1,
          },
        }
      );

      // 이미지 슬라이더 애니메이션
      if (imageSliderRef.current) {
        gsap.fromTo(
          imageSliderRef.current,
          { 
            opacity: 0, 
            y: 60,
            scale: 0.95
          },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 1,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: imageSliderRef.current,
              start: 'top 80%',
              end: 'top 50%',
              scrub: 1,
            },
          }
        );
      }

      // 두 번째 텍스트 애니메이션
      if (textSide2Ref.current) {
        gsap.fromTo(
          textSide2Ref.current,
          { 
            opacity: 0, 
            y: 30
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: textSide2Ref.current,
              start: 'top 85%',
              end: 'top 70%',
              scrub: 1,
            },
          }
        );
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // 이미지 자동 슬라이드
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }, 3000); // 3초마다 전환

    return () => clearInterval(interval);
  }, [images.length]);

  // 비디오가 화면을 벗어나면 자동으로 음소거
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting && videoElementRef.current) {
            // 영상이 화면을 벗어나면 음소거
            videoElementRef.current.muted = true;
            setIsMuted(true);
          }
        });
      },
      {
        threshold: 0.5, // 비디오의 50% 이상이 보일 때
      }
    );

    const currentVideo = videoRef.current;
    if (currentVideo) {
      observer.observe(currentVideo);
    }

    return () => {
      if (currentVideo) {
        observer.unobserve(currentVideo);
      }
    };
  }, []);

  const toggleMute = () => {
    if (videoElementRef.current) {
      videoElementRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <section className="language-secret-section" ref={sectionRef}>
      <div className="language-secret-content container container--wide">
        <h2 className="language-secret-title" ref={titleRef}>
          {t.languageSecret.title}
        </h2>
        
        <div className="content-wrapper">
          <div className="video-side">
            <div className="video-container" ref={videoRef}>
              <video
                className="feature-video"
                autoPlay
                loop
                muted
                playsInline
                ref={videoElementRef}
              >
                <source src="/movie/example.mov" type="video/mp4" />
                {t.common.videoUnsupported}
              </video>
              
              <a 
                href="https://www.youtube.com/watch?v=NiTsduRreug" 
                target="_blank" 
                rel="noopener noreferrer"
                className="source-link"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 6H6C4.89543 6 4 6.89543 4 8V18C4 19.1046 4.89543 20 6 20H16C17.1046 20 18 19.1046 18 18V14M14 4H20M20 4V10M20 4L10 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {t.languageSecret.sourceLink}
              </a>
              
              <button 
                className={`mute-toggle ${isMuted ? 'muted' : 'unmuted'}`}
                onClick={toggleMute}
                aria-label={isMuted ? t.languageSecret.muteOn : t.languageSecret.muteOff}
              >
                {isMuted ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="23" y1="9" x2="17" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="17" y1="9" x2="23" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M15.54 8.46C16.4774 9.39764 17.0039 10.6692 17.0039 11.995C17.0039 13.3208 16.4774 14.5924 15.54 15.53" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M19.07 4.93C20.9447 6.80528 21.9979 9.34836 21.9979 12C21.9979 14.6516 20.9447 17.1947 19.07 19.07" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="text-side" ref={subtitleRef}>
            <p className="quote-text">
              {t.languageSecret.quote1Lines.map((line, idx) => (
                <React.Fragment key={idx}>
                  {isKo && idx === 1 ? (
                    <>
                      <span className="highlight-gradient">좋아하는 콘텐츠</span>에 충분히 노출되는 것이
                    </>
                  ) : (
                    line
                  )}
                  {idx !== t.languageSecret.quote1Lines.length - 1 ? <br /> : null}
                </React.Fragment>
              ))}
            </p>
          </div>
        </div>

        <div className="content-divider"></div>

        <div className="content-wrapper">
          <div className="image-slider-side">
            <div className="image-slider-container" ref={imageSliderRef}>
              <div className="image-slider">
                {images.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={t.languageSecret.sliderAlt(index + 1)}
                    className={`slider-image ${index === currentImageIndex ? 'active' : ''}`}
                  />
                ))}
              </div>
              
              <a 
                href="https://www.teamblind.com/kr/post/%EC%98%81%EC%96%B4%EC%9E%A5%EC%95%A0-%EA%B0%80%EC%A7%84%EC%82%AC%EB%9E%8C%EB%93%A4%EB%A7%8C-%EB%93%A4%EC%96%B4%EC%99%80%EB%9D%BC-o0t721zv" 
                target="_blank" 
                rel="noopener noreferrer"
                className="source-link"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 6H6C4.89543 6 4 6.89543 4 8V18C4 19.1046 4.89543 20 6 20H16C17.1046 20 18 19.1046 18 18V14M14 4H20M20 4V10M20 4L10 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {t.languageSecret.sourceLink}
              </a>
              
              <div className="slider-dots">
                {images.map((_, index) => (
                  <button
                    key={index}
                    className={`slider-dot ${index === currentImageIndex ? 'active' : ''}`}
                    onClick={() => setCurrentImageIndex(index)}
                    aria-label={t.languageSecret.goToImage(index + 1)}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="text-side" ref={textSide2Ref}>
            <p className="quote-text">
              {t.languageSecret.quote2Lines.map((line, idx) => (
                <React.Fragment key={idx}>
                  {isKo && idx === 3 ? (
                    <>
                      좋아하는 영화·드라마로 <span className="highlight-gradient">미믹킹</span>하기
                    </>
                  ) : (
                    line
                  )}
                  {idx !== t.languageSecret.quote2Lines.length - 1 ? <br /> : null}
                </React.Fragment>
              ))}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LanguageSecretSection;
